/**
 * LocalFileSystemClient
 *
 * Implements the StorageService interface for the Electron desktop app.
 * Files are stored in the OS user-data directory via IPC calls to the main process.
 * URLs are served via the custom app://storage/ protocol registered in the main process.
 */
import type { StorageService } from "./StorageServiceFactory";

export class LocalFileSystemService implements StorageService {
  private async getBasePath(): Promise<string> {
    return window.electronAPI!.getStoragePath();
  }

  async uploadFile(file: File, storagePath: string): Promise<string> {
    const basePath = await this.getBasePath();
    const fullPath = `${basePath}/${storagePath}`;

    // Ensure parent directory exists
    const dirPath = fullPath.substring(0, fullPath.lastIndexOf("/"));
    await window.electronAPI!.ensureDir(dirPath);

    // Read file as ArrayBuffer and write to local FS
    const buffer = await file.arrayBuffer();
    await window.electronAPI!.saveFile(fullPath, buffer);

    return storagePath;
  }

  async deleteFile(storagePath: string): Promise<void> {
    const basePath = await this.getBasePath();
    const fullPath = `${basePath}/${storagePath}`;
    await window.electronAPI!.deleteFile(fullPath);
  }

  async getFileUrl(storagePath: string): Promise<string> {
    // The main process registers app://storage/ to serve files from the userData storage dir
    return `app://storage/${storagePath}`;
  }

  async generateUploadUrl(_path: string, _contentType: string): Promise<string> {
    // In local mode there are no presigned URLs — callers should use uploadFile directly
    throw new Error("generateUploadUrl is not supported in local storage mode. Use uploadFile instead.");
  }

  async listFiles(prefix: string): Promise<string[]> {
    // Query the database for files stored under this prefix rather than scanning the FS
    // This is more reliable and consistent with how data is tracked
    const rows = await window.electronAPI!.db.allQuery(
      `SELECT storage_path FROM panoramas WHERE storage_path LIKE ? UNION
       SELECT thumbnail_path FROM panoramas WHERE thumbnail_path LIKE ?`,
      [`${prefix}%`, `${prefix}%`],
    );
    return rows
      .map((r) => (r.storage_path ?? r.thumbnail_path) as string)
      .filter(Boolean);
  }

  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      const basePath = await this.getBasePath();
      await window.electronAPI!.ensureDir(basePath);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Local storage unavailable",
      };
    }
  }
}

/**
 * Returns true when the code is running inside the Electron desktop app.
 */
export function isElectron(): boolean {
  return typeof window !== "undefined" && !!window.electronAPI?.isElectron;
}
